/**
 * Tempo real do chat: UMA camada para o site inteiro.
 *
 * - Assina UM canal privado por usuário (private-user.{id}), em toda página logada que tenha o widget.
 *   (Antes cada tela assinava o canal PÚBLICO da conversa aberta; o admin com o widget fechado não escutava nada.)
 * - Traduz o que chega do socket em eventos do navegador, que as telas ouvem com addEventListener:
 *     chat:message      { message, source: 'socket'|'sync', clientId, mine }
 *     chat:closed       { chatId, message }
 *     chat:reopened     { chatId }
 *     chat:unread       { count, source }
 *     chat:connection   { state, previous }   (connected, connecting, unavailable, disconnected, failed...)
 * - Recupera o que o socket possa ter perdido (aba em segundo plano, queda de rede, reinício do servidor):
 *   chama /chat/widget/updates quando reconecta, quando a aba volta a ficar visível, quando a rede volta
 *   e de tempos em tempos (a cada 2 min conectado, a cada 15 s desconectado).
 * - Não repete mensagem: guarda os ids já entregues.
 *
 * Uso:  window.ChatRealtime.start({ userId, syncUrl })   (o widget faz isso sozinho)
 */

const SEEN_LIMIT = 500;
const SYNC_PAGE = 100; // igual a ChatController::SYNC_LIMIT
const INTERVAL_CONNECTED_MS = 120000;
const INTERVAL_OFFLINE_MS = 15000;

const rt = {
    userId: null,
    syncUrl: null,
    started: false,
    connected: false,
    lastId: null, // maior id de mensagem já visto; null = ainda não inicializado
    seen: new Set(),
    timer: null,
    syncing: false,
};

function emit(name, detail) {
    window.dispatchEvent(new CustomEvent(name, { detail }));
}

function remember(id) {
    rt.seen.add(id);
    if (rt.seen.size > SEEN_LIMIT) {
        rt.seen.delete(rt.seen.values().next().value);
    }
    if (rt.lastId === null || id > rt.lastId) {
        rt.lastId = id;
    }
}

/** Entrega a mensagem às telas uma única vez, venha do socket ou da sincronização. */
function deliver(message, source, clientId) {
    if (!message || rt.seen.has(message.id)) {
        return false;
    }
    remember(message.id);
    emit('chat:message', {
        message,
        source,
        clientId: clientId || null,
        mine: Number(message.sender_id) === Number(rt.userId),
    });
    return true;
}

async function fetchUpdates(sinceId) {
    const url = sinceId === null ? rt.syncUrl : `${rt.syncUrl}?since_id=${sinceId}`;
    const response = await fetch(url, {
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
    });
    if (!response.ok) {
        throw new Error(`sync HTTP ${response.status}`);
    }
    return response.json();
}

/** Busca o que ficou para trás. `reason` é só para depuração. */
async function resync(reason) {
    if (!rt.syncUrl || rt.syncing) {
        return;
    }
    rt.syncing = true;
    try {
        if (rt.lastId === null) {
            // primeira vez: só descobre até onde já foi (não reentrega histórico) e o total de não lidas
            const data = await fetchUpdates(null);
            rt.lastId = data.latest_id || 0;
            emit('chat:unread', { count: data.unread_count || 0, source: 'sync' });
            return;
        }

        let guard = 0;
        let data;
        do {
            data = await fetchUpdates(rt.lastId);
            (data.messages || []).forEach((m) => deliver(m, 'sync'));
            guard++;
        } while ((data.messages || []).length >= SYNC_PAGE && guard < 5);

        emit('chat:unread', { count: data.unread_count || 0, source: 'sync' });
    } catch (e) {
        console.warn('[chat] sincronização falhou (' + reason + '):', e.message);
    } finally {
        rt.syncing = false;
    }
}

function scheduleNext() {
    clearTimeout(rt.timer);
    rt.timer = setTimeout(async () => {
        if (!document.hidden) {
            await resync('timer');
        }
        scheduleNext();
    }, rt.connected ? INTERVAL_CONNECTED_MS : INTERVAL_OFFLINE_MS);
}

function subscribe() {
    if (!window.Echo) {
        console.warn('[chat] Laravel Echo indisponível: o chat só atualiza pela sincronização periódica.');
        return;
    }

    window.Echo.private(`user.${rt.userId}`)
        .listen('.message.sent', (e) => deliver(e.message, 'socket', e.client_id))
        .listen('.chat.closed', (e) => emit('chat:closed', { chatId: Number(e.chat_id), message: e.message }))
        .listen('.chat.reopened', (e) => emit('chat:reopened', { chatId: Number(e.chat_id) }))
        .error((err) => {
            // normalmente 401/403 na autorização do canal (sessão expirada)
            console.warn('[chat] erro ao assinar o canal privado:', err);
            emit('chat:connection', { state: 'auth_error', previous: null });
        });

    const connection = window.Echo.connector && window.Echo.connector.pusher && window.Echo.connector.pusher.connection;
    if (connection) {
        rt.connected = connection.state === 'connected';
        connection.bind('state_change', ({ previous, current }) => {
            rt.connected = current === 'connected';
            emit('chat:connection', { state: current, previous });
            if (current === 'connected' && previous !== 'connected') {
                resync('reconexão'); // o que chegou enquanto estava fora
            }
            scheduleNext();
        });
    }
}

function start({ userId, syncUrl }) {
    if (rt.started) {
        return;
    }
    rt.started = true;
    rt.userId = Number(userId);
    rt.syncUrl = syncUrl;

    subscribe();
    resync('início');
    scheduleNext();

    document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
            resync('aba visível');
        }
    });
    window.addEventListener('online', () => resync('rede voltou'));
}

window.ChatRealtime = {
    start,
    resync,
    /**
     * Marca um id como já visto (histórico carregado, resposta HTTP do próprio envio) para não ser entregue
     * de novo. NÃO avança o marcador de sincronização: só o que chega pelo socket/sincronização o avança,
     * senão uma mensagem perdida de OUTRA conversa ficaria para trás.
     */
    markSeen: (id) => {
        rt.seen.add(id);
        if (rt.seen.size > SEEN_LIMIT) {
            rt.seen.delete(rt.seen.values().next().value);
        }
    },
    /** Id do socket atual: vai no header X-Socket-ID para o servidor não devolver a mensagem a quem enviou. */
    socketId: () => (window.Echo && typeof window.Echo.socketId === 'function' ? window.Echo.socketId() : null),
    get connected() {
        return rt.connected;
    },
};
